import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  Calculator,
  Building,
  Users,
  Clock,
  CheckCircle,
  Check,
  ArrowRight,
  Mail,
  AlertTriangle,
  DollarSign,
  Calendar,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";
import { trackQuoteRequest, trackLeadStart, trackAdsQuoteConversion } from "../utils/analytics";

const API = process.env.REACT_APP_BACKEND_URL;

const QuoteGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null);
  const [formData, setFormData] = useState({
    contact_name: "",
    contact_email: "",
    org_type: "",
    staff_size: "",
    services: [],
    hours_per_month: "",
  });

  const orgTypes = [
    { value: "healthcare_practice", label: "Healthcare Practice/Clinic" },
    { value: "home_health", label: "Home Health/Community Care" },
    { value: "healthcare_startup", label: "Healthcare/Health-Tech Startup" },
    { value: "life_sciences", label: "Life Sciences/Biotech Company" },
  ];

  const staffSizes = [
    { value: "small", label: "3-10 providers / <20 staff" },
    { value: "medium", label: "11-20 providers / 20-50 staff" },
    { value: "large", label: "21+ providers / 51+ staff" },
  ];

  const services = [
    // Consulting Services
    { 
      key: "operational_consulting", 
      name: "Operational Consulting", 
      description: "Workflow optimization and process improvement",
      category: "Consulting"
    },
    { 
      key: "ehr_implementation", 
      name: "EHR Implementation / Go-Live Support", 
      description: "EHR selection, implementation planning, data migration, staff training, and go-live support",
      category: "Consulting"
    },
    { 
      key: "patient_experience", 
      name: "Patient Experience & Growth", 
      description: "Patient satisfaction and practice growth strategies",
      category: "Consulting"
    },
    { 
      key: "workforce_hr", 
      name: "Workforce/HR & Culture", 
      description: "Team development and organizational culture",
      category: "Consulting"
    },
    { 
      key: "compliance", 
      name: "Compliance Readiness", 
      description: "Regulatory compliance and audit preparation",
      category: "Consulting"
    },
    { 
      key: "licensing_credentialing", 
      name: "Licensing & Credentialing", 
      description: "Professional licensing, credentialing, and enrollment services",
      category: "Consulting"
    },
  ];

  const suggestedHours = [20, 40, 60, 80];

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setQuote(null); // Reset quote when form changes
  };

  const toggleService = (serviceKey) => {
    setFormData((prev) => {
      const currentServices = prev.services;
      const newServices = currentServices.includes(serviceKey)
        ? currentServices.filter((s) => s !== serviceKey)
        : [...currentServices, serviceKey];
      return { ...prev, services: newServices };
    });
    setQuote(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.contact_name || !formData.contact_email) {
      toast.error("Please enter your name and email");
      return;
    }
    if (!formData.org_type) {
      toast.error("Please select your organization type");
      return;
    }
    if (!formData.staff_size) {
      toast.error("Please select your staff size");
      return;
    }
    if (formData.services.length === 0) {
      toast.error("Please select at least one service");
      return;
    }
    if (!formData.hours_per_month || parseInt(formData.hours_per_month) < 1) {
      toast.error("Please enter valid hours per month");
      return;
    }

    setLoading(true);

    try {
      const params = new URLSearchParams({
        contact_name: formData.contact_name,
        contact_email: formData.contact_email,
        org_type: formData.org_type,
        staff_size: formData.staff_size,
        services: formData.services.join(","),
        hours_per_month: formData.hours_per_month,
      });

      const response = await fetch(`${API}/api/quote/calculate?${params.toString()}`, {
        method: "POST",
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.detail || "Failed to calculate quote");

      setQuote(data);
      // Track quote request with estimated value
      trackQuoteRequest(data.estimated_total || 0);
      trackAdsQuoteConversion();
      toast.success("Quote calculated! Check your email for details.");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <SEO 
        title="Get an Instant Quote - Healthcare Consulting Services"
        description="Answer a few quick questions about your practice and get a personalized consulting estimate. Operations help, EHR implementation, compliance support, team building and more."
        keywords="healthcare consulting cost, medical practice consulting pricing, how much does healthcare consulting cost, practice management quote, clinic operations consulting estimate"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Get a Quote", "item": "https://www.rnbconsultinggroup.com/quote" }
        ]
      }]} />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-rnb-light to-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-teal/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-flex items-center px-4 py-2 bg-rnb-teal/10 rounded-full mb-6">
              <Calculator className="w-4 h-4 text-rnb-teal mr-2" />
              <span className="text-sm font-medium text-rnb-navy">Instant Quote</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-rnb-navy mb-6">
              Get Your Free Quote
            </h1>

            <p className="text-lg text-rnb-gray mb-4">
              Curious about the cost? Tell us a bit about your practice and we&apos;ll give you 
              an instant estimate—no strings attached. We&apos;ll also email you the details.
            </p>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Form */}
            <div className="lg:col-span-3">
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Contact Info */}
                    <div className="space-y-4">
                      <h3 className="font-heading text-lg font-semibold text-rnb-navy flex items-center gap-2">
                        <Mail className="w-5 h-5 text-rnb-teal" />
                        Contact Information
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Your Name *</Label>
                          <Input
                            value={formData.contact_name}
                            onChange={(e) => handleChange("contact_name", e.target.value)}
                            placeholder="Full name"
                            data-testid="quote-name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Email Address *</Label>
                          <Input
                            type="email"
                            value={formData.contact_email}
                            onChange={(e) => handleChange("contact_email", e.target.value)}
                            placeholder="you@example.com"
                            data-testid="quote-email"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Organization Type */}
                    <div className="space-y-4">
                      <h3 className="font-heading text-lg font-semibold text-rnb-navy flex items-center gap-2">
                        <Building className="w-5 h-5 text-rnb-teal" />
                        What type of organization are you?
                      </h3>
                      <Select
                        value={formData.org_type}
                        onValueChange={(value) => handleChange("org_type", value)}
                      >
                        <SelectTrigger data-testid="quote-org-type">
                          <SelectValue placeholder="Select organization type..." />
                        </SelectTrigger>
                        <SelectContent>
                          {orgTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(formData.org_type === "life_sciences" || formData.org_type === "healthcare_startup") && (
                        <p className="text-xs text-rnb-teal">
                          Life Sciences & Health-Tech organizations include a 10% premium on consulting services.
                        </p>
                      )}
                    </div>

                    {/* Staff Size */}
                    <div className="space-y-4">
                      <h3 className="font-heading text-lg font-semibold text-rnb-navy flex items-center gap-2">
                        <Users className="w-5 h-5 text-rnb-teal" />
                        How many providers or staff do you have?
                      </h3>
                      <Select
                        value={formData.staff_size}
                        onValueChange={(value) => handleChange("staff_size", value)}
                      >
                        <SelectTrigger data-testid="quote-staff-size">
                          <SelectValue placeholder="Select staff size..." />
                        </SelectTrigger>
                        <SelectContent>
                          {staffSizes.map((size) => (
                            <SelectItem key={size.value} value={size.value}>
                              {size.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Services */}
                    <div className="space-y-4">
                      <h3 className="font-heading text-lg font-semibold text-rnb-navy flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-rnb-teal" />
                        Which service(s) are you interested in?
                      </h3>
                      <p className="text-sm text-rnb-gray">Select all that apply</p>
                      
                      <div className="space-y-3">
                        {services.map((service) => (
                          <div
                            key={service.key}
                            className={`border rounded-lg p-4 cursor-pointer transition-all select-none ${
                              formData.services.includes(service.key)
                                ? "border-rnb-teal bg-rnb-teal/5"
                                : "hover:border-slate-300"
                            }`}
                            onClick={() => toggleService(service.key)}
                            data-testid={`quote-service-${service.key}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`h-4 w-4 shrink-0 rounded-sm border shadow mt-0.5 flex items-center justify-center ${
                                formData.services.includes(service.key)
                                  ? "bg-rnb-teal border-rnb-teal text-white"
                                  : "border-slate-300"
                              }`}>
                                {formData.services.includes(service.key) && (
                                  <Check className="h-3 w-3" />
                                )}
                              </div>
                              <div className="flex-1">
                                <span className="font-medium text-rnb-navy">
                                  {service.name}
                                </span>
                                <p className="text-sm text-rnb-gray mt-1">{service.description}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      
                    </div>

                    {/* Hours */}
                    <div className="space-y-4">
                      <h3 className="font-heading text-lg font-semibold text-rnb-navy flex items-center gap-2">
                        <Clock className="w-5 h-5 text-rnb-teal" />
                        How many hours of support do you need per month?
                      </h3>
                      <div className="space-y-3">
                        <Input
                          type="number"
                          min="1"
                          value={formData.hours_per_month}
                          onChange={(e) => handleChange("hours_per_month", e.target.value)}
                          placeholder="Enter hours per month"
                          data-testid="quote-hours"
                        />
                        <div className="flex gap-2 flex-wrap">
                          <span className="text-sm text-rnb-gray">Suggested:</span>
                          {suggestedHours.map((hours) => (
                            <button
                              key={hours}
                              type="button"
                              onClick={() => handleChange("hours_per_month", hours.toString())}
                              className={`px-3 py-1 text-sm rounded-full transition-colors ${
                                formData.hours_per_month === hours.toString()
                                  ? "bg-rnb-teal text-white"
                                  : "bg-slate-100 text-rnb-navy hover:bg-slate-200"
                              }`}
                            >
                              {hours} hrs
                            </button>
                          ))}
                        </div>
                        {parseInt(formData.hours_per_month) >= 40 && parseInt(formData.hours_per_month) < 60 && (
                          <p className="text-xs text-rnb-teal flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            5% volume discount applied for 40+ hours! (Consulting services only)
                          </p>
                        )}
                        {parseInt(formData.hours_per_month) >= 60 && (
                          <p className="text-xs text-rnb-teal flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" />
                            25% volume discount applied for 60+ hours! (Consulting services only)
                          </p>
                        )}
                      </div>
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full btn-hover-lift bg-rnb-teal hover:bg-rnb-teal/90"
                      disabled={loading}
                      data-testid="calculate-quote-btn"
                    >
                      {loading ? "Calculating..." : "Get My Instant Quote"}
                      <Calculator className="ml-2 h-4 w-4" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Quote Result / Info Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              {quote ? (
                <Card className="border-2 border-rnb-teal shadow-lg sticky top-8">
                  <CardContent className="p-6">
                    <div className="text-center mb-6">
                      <CheckCircle className="w-12 h-12 text-rnb-teal mx-auto mb-3" />
                      <h3 className="font-heading text-xl font-bold text-rnb-navy">
                        Your Estimated Quote
                      </h3>
                      <p className="text-sm text-rnb-gray">
                        Sent to {formData.contact_email}
                      </p>
                    </div>

                    <div className="text-center py-4 bg-rnb-light rounded-lg mb-6">
                      <p className="text-sm text-rnb-gray mb-1">Estimated Monthly Total</p>
                      <p className="font-mono-numbers text-4xl font-bold text-rnb-navy">
                        ${quote.monthly_total.toLocaleString()}
                      </p>
                      <p className="text-sm text-rnb-gray">/month</p>
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex justify-between text-sm">
                        <span className="text-rnb-gray">Organization</span>
                        <span className="text-rnb-navy font-medium">{quote.org_type}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-rnb-gray">Staff Size</span>
                        <span className="text-rnb-navy font-medium">{quote.staff_size}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-rnb-gray">Hours/Month</span>
                        <span className="text-rnb-navy font-medium">{quote.hours_per_month}</span>
                      </div>
                      
                      {/* Price breakdown */}
                      <div className="border-t pt-3 mt-3 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-rnb-gray">Subtotal</span>
                          <span className="text-rnb-navy font-medium">${quote.subtotal?.toLocaleString() || '0'}</span>
                        </div>
                        {quote.premium_amount > 0 && (
                          <div className="flex justify-between text-sm text-rnb-gray">
                            <span>Org Type Premium (10%)</span>
                            <span>+${quote.premium_amount?.toLocaleString() || '0'}</span>
                          </div>
                        )}
                        {quote.volume_discount > 0 && (
                          <div className="flex justify-between text-sm text-rnb-teal">
                            <span>Volume Discount ({quote.volume_discount_percentage}%)</span>
                            <span className="font-medium">-${quote.volume_discount.toLocaleString()}</span>
                          </div>
                        )}
                        {quote.volume_discount > 0 && (
                          <div className="flex justify-between text-sm pt-2 border-t border-dashed">
                            <span className="text-rnb-navy font-semibold">You Save</span>
                            <span className="text-rnb-teal font-bold">${quote.volume_discount.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="border-t pt-4 mb-6">
                      <p className="text-sm font-medium text-rnb-navy mb-2">Services:</p>
                      <ul className="space-y-2">
                        {quote.services.map((service, idx) => (
                          <li key={service.key} className="flex justify-between text-sm">
                            <span className={`text-rnb-gray ${service.key === quote.highest_service ? "font-medium" : ""}`}>
                              {service.name}
                              {service.key === quote.highest_service && quote.services.length > 1 && (
                                <span className="ml-1 text-xs text-rnb-teal">(applied rate)</span>
                              )}
                            </span>
                            <span className="text-rnb-navy">${service.rate}/hr</span>
                          </li>
                        ))}
                      </ul>
                      {quote.services.length > 1 && (
                        <p className="text-xs text-rnb-gray mt-3 italic">
                          * When selecting multiple services, you are only charged the rate of the highest-priced service.
                        </p>
                      )}
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
                      <p className="text-xs text-yellow-800 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span>
                          <strong>Disclaimer:</strong> This is an estimated rate. Please speak with our team for an official quoted rate based on your specific needs.
                        </span>
                      </p>
                    </div>

                    <Link to="/consultation">
                      <Button className="w-full btn-hover-lift">
                        <Calendar className="mr-2 h-4 w-4" />
                        Get Official Quote
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-0 shadow-sm sticky top-8">
                  <CardContent className="p-6">
                    <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-4">
                      Why Get a Quote?
                    </h3>
                    <ul className="space-y-4">
                      <li className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-rnb-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <DollarSign className="w-4 h-4 text-rnb-teal" />
                        </div>
                        <div>
                          <p className="font-medium text-rnb-navy text-sm">Transparent Pricing</p>
                          <p className="text-xs text-rnb-gray">Know your costs upfront with no surprises</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-rnb-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Calculator className="w-4 h-4 text-rnb-teal" />
                        </div>
                        <div>
                          <p className="font-medium text-rnb-navy text-sm">Customized Estimates</p>
                          <p className="text-xs text-rnb-gray">Rates based on your specific needs</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-rnb-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Mail className="w-4 h-4 text-rnb-teal" />
                        </div>
                        <div>
                          <p className="font-medium text-rnb-navy text-sm">Instant Email</p>
                          <p className="text-xs text-rnb-gray">Quote details sent directly to you</p>
                        </div>
                      </li>
                      <li className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-rnb-teal/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-4 h-4 text-rnb-teal" />
                        </div>
                        <div>
                          <p className="font-medium text-rnb-navy text-sm">Volume Discounts</p>
                          <p className="text-xs text-rnb-gray">Save 5% on 40+ hrs, 25% on 60+ hrs (consulting)</p>
                        </div>
                      </li>
                    </ul>

                    <div className="mt-6 pt-6 border-t">
                      <p className="text-sm text-rnb-gray mb-3">
                        Need help choosing services?
                      </p>
                      <Link to="/consultation">
                        <Button variant="outline" size="sm" className="w-full">
                          Schedule Free Consultation
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl p-6 shadow-sm">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
                  Important Disclaimer
                </h3>
                <p className="text-rnb-gray">
                  The quote provided by this tool is an <strong>estimated rate</strong> based on the general 
                  information you provide. Actual pricing may vary based on specific project requirements, 
                  scope, complexity, and customization needs. To receive an <strong>official quoted rate</strong>, 
                  please schedule a consultation with one of our team members who can assess your unique 
                  situation and provide accurate pricing.
                </p>
                <div className="mt-4">
                  <Link to="/consultation">
                    <Button>
                      Schedule Consultation for Official Quote
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default QuoteGenerator;
